/**
 * @module msysGeo
 * @description
 * Mosy Geo library
 * Provides methods related to geography and maps
 * Example of use:
 * Note that all functions support point inputs in any one of 4 formats:
 * - geohash string
 * - lat,lng string
 * - [lat,lng] array
 * - {lat: lat, lng: lng} object
 *
 * ```
 * var geo = require('msysGeo');
 *
 * // geographic methods
 * var geohash = geo.toGeohash(point); // transforms point from any format to geohash
 * var latlng = geo.toLatLng(point); // transforms point from any format to [lat,lng]
 * var meters = geo.distance(point1, point2);
 *
 * // addressing methods
 * var geodata = require('raw-loader!./slovakia-latest.osm.txt');
 * geo.loadGeoData(geodata); // needed before other addressing methods; loads geodata generated by src/tools/pbf2mosy3.js
 * var addr = geo.getAddress('u2s4n8cm3x');
 * var addr = geo.getAddress('48.169124,17.162431');
 * var addr = geo.getAddress([48.169124,17.162431]);
 * var results = geo.search('u2s4n8cm3x');
 * var results = geo.search('48.169124,17.162431');
 * var results = geo.search('bb Billa');
 * var results = geo.search('vajnorska ba');
 * var results = geo.search('bratis eisn 26');
 * ```
 *
 * Implementation notes
 * Geohashes use precision of 10 base32 characters.
 *
 * Input geodata file is .txt file with 3 types of lines:
 * ```
 * alias line: name:alias1,alias2,alias3,...
 * street line: city|street|center|shape|address1|address2|...
 *   center: 10-char geohash
 *   shape: shapetype,geohash,geohash,...,shapetype,geohash,geohash
 *     shapetype: A - area (closed polygon), P - path (open polyline)
 *   address: streetno;center;shape
 * site line: id|center|address|type|name(s)
 *   center - 10-char geohash of site's center
 *   address - 10-char geohash of address belonging to the site
 *   type - one of: police,justice,fire,rescue,doctor,public,temple,school,bank,post,office,leisure
 *   name(s) - comma separated names
 * ```
 */

const geo = module.exports = {}
const _ = require('lodash')
const ngeohash = require('ngeohash')
const LANG = require('../../res/lang/lang.js')
const GEOHASH_PRECISION = 10
const GEOHASH_SPLIT = 5

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * hash of aliases
 * key: alias
 * value: replacement name
 */
const geo_aliases = {} //

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * list of streets
 * each item is hash with following keys:
 *   c: contains city name
 *   s: contains street name
 *   x: center of street as geohash
 *   w: shape of street
 *   a: list of addresses on the street
 *   f: list of two searching strings; built as 'city street' and 'street city' normalized for searching (removed accents, removed non-letter chars, lowercased)
 */
const geo_streets = []

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * list of sites (named places)
 * each item is hash with following keys:
 *   i: contains internal site ID
 *   x: center of site's
 *   a: center of associated address
 *   t: type of site; one of police,justice,fire,rescue,doctor,public,temple,school,bank,post,office,leisure
 *   n: comma separated names for the place
 *   f: list of six searching strings; built as comma separated name,city,street combinations in different order normalized for searching (removed accents, removed non-letter chars, lowercased)
 */
const geo_sites = [] // list of sites

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * index for searching through position
 * key: beginning part of geohash with length of GEOHASH_SPLIT
 * value: string containing lines, each line for either street or site
 *   street line: remain of geohash:street_idx:address_idx
 *   site line: remain of geohash:site_idx
 * street_idx is order of the street in geo_streets
 * address_idx is order of the address in geo_streets[street_idx].a
 * site_idx is order of the site in geo_sites
 */
const geo_index = {} // index for searching through position; keys are first part of geohash (with length of GEOHASH_SPLIT), values are strings with lines each containing: second part of geohash:street_idx:address_idx or second part of geohash:site_idx

geo.memorySize = function() {
  return {
    index: _.memorySize(geo_index),
    streets: _.memorySize(geo_streets),
    aliases: _.memorySize(geo_aliases),
    sites: _.memorySize(geo_sites),
  }
} // memorySize

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * transform point from any format to [lat,lng] array
 * returns null on failure
 */
geo.toLatLng = function(point) {
  let lat, lng
  if(typeof (point)==='string') {
    let m = point.match(/^(\d+(?:\.\d*)?)\s*(?:,|:|;|\s+)\s*(\d+(?:\.\d*)?)/)
    if(!m) {
      m = point.match(/^(\d+)°\s*(\d+)'\s*(\d+(?:\.\d+)?)"\s*([nNsS])\s*(\d+)°\s*(\d+)'\s*(\d+(?:\.\d+)?)"\s*([eEwW])$/)
      if(m) {
        m = [
          null,
          parseInt(m[1])+m[2]/60+m[3]/3600*(/n/i.test(m[4])?1:-1),
          parseInt(m[5])+m[6]/60+m[7]/3600*(/e/i.test(m[8])?1:-1),
        ]
      }
    }
    if(m) {
      // string containing latitude and longitude
      lat = parseFloat(m[1])
      lng = parseFloat(m[2])
    }else if(/^\w{1,20}$/.test(point)) {
      // string is geohash; decode it
      const latlng = ngeohash.decode(point)
      if(latlng) {
        lat = latlng.latitude
        lng = latlng.longitude
      }
    }
  }else if(Array.isArray(point)) {
    if(point.length===2) {
      lat = parseFloat(point[0])
      lng = parseFloat(point[1])
    }
  }else if(_.isObjectLike(point)) {
    lat = parseFloat(point.lat || point.latitude)
    lng = parseFloat(point.lng || point.longitude)
  }

  if(isNaN(lat) || isNaN(lng)) {
    return null
  }else{
    lat = _.roundBy(Math.floor(lat*1000000) / 1000000, 6)
    lng = _.roundBy(Math.floor(lng*1000000) / 1000000, 6)
    return [lat, lng]
  }
} // toLatLng

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * transform point from any format to geohash of maximum GEOHASH_PRECISION length
 * returns empty string on failure
 */
geo.toGeohash = function(point, precision) {
  precision = precision || GEOHASH_PRECISION
  if((typeof (point)==='string' || typeof (point)==='number') && /^\w{1,20}$/.test(point)) {
    return String(point).substr(0, precision)
  }
  const latlng = geo.toLatLng(point)
  if(latlng) { return ngeohash.encode(latlng[0], latlng[1], precision) }
  return ''
} // toGeohash

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * Calculates distance between two points in meters.
 * If any of points is invalid, return null.
 */
geo.distance = function(p1, p2) {
  p1 = geo.toLatLng(p1)
  p2 = geo.toLatLng(p2)
  if(p1.length === 0 || p2.length === 0) return null

  const [lat1, lng1] = p1
  const [lat2, lng2] = p2
  const lat1rad = lat1 * Math.PI / 180
  const lat2rad = lat2 * Math.PI / 180
  const dLat = (lat2-lat1) * Math.PI / 180
  const dLong = (lng2-lng1) * Math.PI / 180

  const R = 6378137 // Earth’s mean radius in meters
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1rad) * Math.cos(lat2rad) *
    Math.sin(dLong / 2) * Math.sin(dLong / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const d = R * c
  return Math.abs(d) // returns the distance in meters
} // distance

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * translates all characters to ASCII lowercase and replace non-letters with space
 */
function _cleanForIndex(str) {
  str = LANG.removeAccents(str).replace(/\b(\d+)\/(\w)/g, '$1$2')
  if(!/UA/i.test(MOSY_CUSTOMER)) str = str.replace(/\W+/g, ' ')
  str = str.toLowerCase()
  return str
} // _cleanForIndex

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * replaces end of base geohash with the value geohash
 * e.g. (u2s4n8cm3x,abcd) -> u2s4n8abcd
 */
function _undiffGeohash(base, value) {
  base = _.toString(base)
  value = _.toString(value)
  if(base.length===0) return value
  return base.substr(0, base.length-value.length) + value
} // _undiffGeohash

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * "unzip" compressed shape format
 */
function _decompressShape(center, shape) {
  if(!shape) return ''
  let last = center
  return shape.split(',').map(part=>{
    if(/^[A-Z]$/.test(part)) {
      // shape type, do not touch
      return part
    }else{
      // geohash was encoded as difference from the previous geohash
      last = _undiffGeohash(last, part)
      return last
    }
  }).join(',')
} // _decompressShape

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * sort geo objects; named places first, then addresses; sorted by name,street,streetno,city
 */
function _sortGeo(a, b) {
  return(
    // named places are displayed first
    a.name && !b.name ? -1 :
      !a.name && b.name ? 1 :

      // else sort by name, street, streetno, city
        (
          _cmpString(a.name, b.name) ||
      _cmpString(a.street, b.street) ||
      _cmpStreetNo(a.streetno, b.streetno) ||
      _cmpString(a.city, b.city)
        )
  )
} // _sortGeo

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * compare as string
 */
function _cmpString(a, b) {
  a = _.toString(a)
  b = _.toString(b)
  return(a<b ? -1 : a>b ? 1 : 0)
} // _cmpString

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * compare street numbers
 * e.g. 2 < 2A < 2D < 24 < 24B
 */
function _cmpStreetNo(a, b) {
  return(
    (parseInt(a)-parseInt(b)) ||
    _cmpString(a, b)
  )
} // _cmpStreetNo

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * generate regexp for searching the keyword including its aliases
 * e.g. 2 < 2A < 2D < 24 < 24B
 */
function _regexpKeywordWithAliases(key) {
  if(/UA/i.test(MOSY_CUSTOMER)) {
    return geo_aliases[key] ? '\\b(?:'+key+'.*|'+geo_aliases[key]+'(?:\\b|\\s))' : ''+key+'.*'
  }else{
    return geo_aliases[key] ? '\\b(?:'+key+'.*|'+geo_aliases[key]+'(?:\\b|\\s))' : '\\b'+key+'.*'
  }
} // _regexpKeywordWithAliases

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * load geo data from the string
 */
geo.loadGeoData = function(geo_data, chunk_size, chunk_delay) {
  const re = /(.*)\n/g
  let m

  const parseGroup = ()=>{
    let count = chunk_size || 1000
    while(count>0 && (m = re.exec(geo_data))!=null) {
      count--
      const def = m[1].split('|')
      if(def.length===1) {
        _parseAlias(def[0])
      }else if(/^\d+$/.test(def[0])) {
        _parseSite(def)
      }else {
        _parseStreet(def)
      }
    }
    if(m) setTimeout(parseGroup, chunk_delay || 100)
  }

  parseGroup()
} // loadGeoData

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * add street geo data
 */
geo.addStreet = function(city, street, center, shape, addresses) {
  // construct search index of city/street names
  const street_idx = geo_streets.length
  geo_streets.push({
    c: city,
    s: street,
    x: center,
    w: shape,
    a: addresses,
    f: [
      // texts used for searching (lowercase, no accents, no special chars)
      _cleanForIndex(city+' '+street),
      _cleanForIndex(street+' '+city),
    ],
  })

  if(!Array.isArray(addresses)) return

  // construct reverse geo index:
  addresses.forEach((addr, addr_idx)=>{
    let [, addr_center] = addr.split(';')
    addr_center = _undiffGeohash(center, addr_center)
    const a1 = addr_center.substr(0, GEOHASH_SPLIT)
    const a2 = addr_center.substr(GEOHASH_SPLIT)
    if(!geo_index[a1]) geo_index[a1] = ''
    geo_index[a1] += a2+':'+street_idx + ':' +addr_idx+'\n'
  })
} // addStreet

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * parse alias definition from geo file line
 */
function _parseAlias(def) {
  def = def.match(/^(.+?):(.+)$/)
  if(!def) return
  const name = _cleanForIndex(def[1])
  const aliases = def[2].split(/\s*,\s*/)
  aliases.forEach(alias=>{
    geo_aliases[_cleanForIndex(alias)] = name
  })
} // _parseAlias

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * parse street definition from geo file line
 */
function _parseStreet(def) {
  const [city, street, center, shape] = def
  const addresses = def.slice(4)

  // construct search index of city/street names
  const street_idx = geo_streets.length
  geo_streets.push({
    c: city,
    s: street,
    x: center,
    w: shape,
    a: addresses,
    f: [
      // texts used for searching (lowercase, no accents, no special chars)
      _cleanForIndex(city+' '+street),
      _cleanForIndex(street+' '+city),
    ],
  })

  // construct reverse geo index:
  addresses.forEach((addr, addr_idx)=>{
    let [, addr_center] = addr.split(';')
    addr_center = _undiffGeohash(center, addr_center)
    const a1 = addr_center.substr(0, GEOHASH_SPLIT)
    const a2 = addr_center.substr(GEOHASH_SPLIT)
    if(!geo_index[a1]) geo_index[a1] = ''
    geo_index[a1] += a2+':'+street_idx + ':' +addr_idx+'\n'
  })
} // _parseStreet

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * parse site definition from geo file line
 */
function _parseSite(def) {
  let [id, center, address_center, type, names] = def
  names = names.split(/\s*[;,]\s*/)

  // construct search texts
  let search_strings = []
  const addr = geo.getAddress(address_center||center)
  names.forEach(name=>{
    if(addr) {
      const street = addr.street
      const city = addr.city
      search_strings = search_strings.concat([
        _cleanForIndex(name+' '+street+' '+city),
        _cleanForIndex(name+' '+city+' '+street),
        _cleanForIndex(street+' '+name+' '+city),
        _cleanForIndex(street+' '+city+' '+name),
        _cleanForIndex(city+' '+name+' '+street),
        _cleanForIndex(city+' '+street+' '+name),
      ])
    }else{
      search_strings.push(_cleanForIndex(name))
    }
  })

  // construct search index of site/street/city names
  const site_idx = geo_sites.length
  geo_sites.push({
    i: id,
    x: center,
    a: address_center,
    t: type,
    n: names[0],
    f: search_strings,
  })

  // construct reverse geo index:
  const a1 = center.substr(0, GEOHASH_SPLIT)
  const a2 = center.substr(GEOHASH_SPLIT)
  if(!geo_index[a1]) geo_index[a1] = ''
  geo_index[a1] += a2+':'+site_idx+'\n'
} // _parseSite

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * helper method that formats output object structure for address from street+address idx
 * if distance_point is provided, also appends information on the distance from that point
 */
function _getAddressFromIdx(street_idx, address_idx, distance_point) {
  const street_record = geo_streets[street_idx]
  const address_record = street_record.a[address_idx]
  let [streetno, center, shape] = address_record.split(';')
  center = _undiffGeohash(street_record.x, center)
  shape = _decompressShape(center, shape)

  const ret = {
    id: center,
    type: 'address',
    city: street_record.c,
    street: street_record.s,
    streetno,
    center,
  }

  if(shape) ret.shape=shape
  if(distance_point) ret.distance = geo.distance(distance_point, center)

  return ret
} // _getAddressFromIdx

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * searches for address matching given point
 */
geo.getAddress = function(point) {
  point = geo.toGeohash(point)
  if(String(point??'')==='') return null

  const a1 = point.substr(0, GEOHASH_SPLIT)
  if(!geo_index[a1]) return null

  const re = new RegExp('^'+point.substr(GEOHASH_SPLIT)+'\\w*:(\\d+):(\\d+)', 'm')
  const match = re.exec(geo_index[a1])
  if(!match) return null
  const [, street_idx, address_idx] = match
  return _getAddressFromIdx(street_idx, address_idx)
} // getAddress

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * search in geo data
 */
geo.search = function(str, types) {
  str = _.toString(str)
  types = Array.isArray(types) ? types.filter(type=>typeof (type)==='string') : _.toString(types).split(/\W+/)
  if(str==='') return []
  const m = str.match(/(\d+(?:\.\d+))[\s/,;:]+(\d+(?:\.\d+))/)
  let results
  if(m) {
    // search "lat,lng"
    const geohash = ngeohash.encode(m[1], m[2], GEOHASH_PRECISION)
    results = geo.searchGeohash(geohash, 3)
    if(results.length===0) results = geo.searchGeohash(geohash, 4)
    if(results.length===0) results = geo.searchGeohash(geohash, 5)
  }else{
    results = []
    if(/^\w{10}$/.test(str)) {
      // search geohash
      results = geo.searchGeohash(str, 3)
      if(results.length===0) results = geo.searchGeohash(str, 4)
      if(results.length===0) results = geo.searchGeohash(str, 5)
    }
    // search  by city, street, streetno and name
    if(results.length===0) { results = _searchByText(str) }
  }

  types = types.filter(type=>type.length>0)
  if(types.length>0) {
    const types_hash = {}
    types.forEach(type=>{ types_hash[type]=1 })
    results = results.filter(result=>types_hash[result.type])
  }

  return results
} // search

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * @description
 * search nearby addresses by geohash
 * @param {string} center - center where to search
 * @param {number} precision - 0=exact match, 1= +/- 1.2/0.6m; 2= 4.8m, 3=38x19m 4=152m 5=1.2km/0.6km 6=4.9km
 */
geo.searchGeohash = function(center, precision, only_address) {
  if(center.length>GEOHASH_PRECISION) center = center.substr(0, GEOHASH_PRECISION)
  const a1 = center.substr(0, GEOHASH_SPLIT)
  if(!geo_index[a1]) return []
  let results = []
  precision = _.natural(precision)

  const search_latlng = ngeohash.decode(center)

  const center_stripped = center.substr(0, center.length-precision)
  const geohashes = precision ? ngeohash.neighbors(center_stripped).concat(center_stripped) : [center]
  const re =
    only_address ? new RegExp('^('+geohashes.map(hash=>hash.substr(GEOHASH_SPLIT)).join('|')+')\\w*:(\\d+)(:(\\d+))', 'mg') :
      new RegExp('^('+geohashes.map(hash=>hash.substr(GEOHASH_SPLIT)).join('|')+')\\w*:(\\d+)(:(\\d+))?', 'mg')
  let m
  while((m=re.exec(geo_index[a1]))!=null) {
    const id = m[2]
    const id2 = m[4]
    if(id2) {
      // it is city|street
      const street = geo_streets[id]
      const addr = street.a[id2]
      let streetno, shape
      [streetno, center, shape] = addr.split(';')
      center = _undiffGeohash(street.x, center)
      shape = _decompressShape(center, shape)
      const addr_latlng = ngeohash.decode(center)
      const distance = geo.distance([search_latlng.latitude, search_latlng.longitude], [addr_latlng.latitude, addr_latlng.longitude])
      results.push({
        id: center,
        type: 'address',
        city: street.c,
        street: street.s,
        streetno,
        center,
        shape: shape||'',
        distance: _.roundBy(distance, 3),
      })
    }else{
      // it is site ID
      const site = geo_sites[id]
      if(!site) continue
      const ret = geo.getAddress(site.a || site.x) || {}
      ret.id = site.x+'.'+site.i
      ret.name = site.n
      ret.type = site.t
      ret.center = site.x
      ret.name = site.n
      ret.distance = geo.distance(search_latlng, site.x)
      results.push(ret)
    }
  }

  results = results.sort((a, b)=>a.distance-b.distance)

  return results
} // searchGeohash

geo.approximateGeohash = function(value, precision = 4) {
  value = _.toString(value).match(/^((\w+)(?:\.\w*)?)(?:;([\w,]*))?(?:\/(.*))?$/)
  if(value) {
    let [, , center, shape, legend] = value
    if(!legend && center) {
      const addresses = geo.searchGeohash(center, precision, true)
      if(addresses.length>0) {
        const street = _.toString(addresses[0].street)
        const city = _.toString(addresses[0].city)
        legend = street+city ? street+', '+city : street+city
      }
    }
    legend = _.toString(legend).split('∫')
    let legend_text
    if(shape) {
      legend_text =
        legend[0] ? _.joinStrings(', ', legend[0], _.joinStrings(' ', legend[1], _.joinStrings('/', legend[2], legend[3])), _.joinStrings(' ', legend[5], legend[4])) :
          _.joinStrings(', ', legend[1], legend[4])
      value = legend_text==='' ? '' : '{{around}} ' +legend_text
    }else{
      legend_text = _.joinStrings(', ', legend[0], _.joinStrings(' ', legend[1], _.joinStrings('/', legend[2], legend[3])), _.joinStrings(' ', legend[5], legend[4]))
      value = legend_text==='' ? '' : '{{}}'+legend_text // to prevent translation of the text
    }
  }
  return value
}

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * @description
 * search addresses/places by matching text
 * @param {string} str - text to be searched
 */
function _searchByText(str) {
  str = _cleanForIndex(_.toString(str))
  if(str==='' || str===' ') return []
  const keywords = str.split(/\s+/).filter(term=>term.length>0)

  let result = _searchAddressesByKeywords(keywords)
  result = result.concat(_searchSitesByKeyword(keywords))
  return result.sort(_sortGeo)
} // _searchByText

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * @description
 * search addresses by matching keywords
 * @param {array} keywords - list of keywords to be searched
 */
function _searchAddressesByKeywords(keywords) {
  let matches = _searchStreetsByKeywords(keywords)

  if(matches.length===0) {
    // no street found using all keywords
    // for all keywords: use one keyword for checking streetno and rest for finding street
    if(keywords.length<2) return [] // must be at least 2 keywords

    for(let k=0; k<keywords.length; k++) {
      // keyword used for matching streetno
      const streetno_keyword = keywords[k]
      if(!/\d/.test(streetno_keyword)) continue // we try to use for streetno only keywords containing some digit

      // find streets using all the keywords except currently examined
      const street_keywords = keywords.slice()
      street_keywords.splice(k, 1)
      const matching_streets = _searchStreetsByKeywords(street_keywords)

      // use currently examined keyword to match streetno within matched streets
      matching_streets.forEach(street=>{
        matches = matches.concat(_searchStreetNo(street, streetno_keyword))
      })
    }
  }else if(matches.length===1) {
    // just 1 street found, include street numbers in results too
    matches = matches.concat(_searchStreetNo(matches[0]))
  }

  matches.forEach(m=>delete m.addresses) // remove addresses info from results

  return matches
} // _searchAddressesByKeywords

function _searchStreetNo(street, str) {
  str = _.toString(str)
  const re = str==='' ? null : new RegExp('^'+str, 'i')
  if(!Array.isArray(street.addresses)) return []
  const matches = []
  street.addresses.forEach(addr=>{
    let [streetno, center, shape] = addr.split(';')
    if(!re || re.test(streetno.replace(/\W+/g, ''))) {
      center = _undiffGeohash(street.center, center)
      shape = _decompressShape(center, shape)
      matches.push({
        id: center,
        type: 'address',
        street: street.street,
        streetno,
        city: street.city,
        center,
        shape: shape||'',
      })
    }
  })
  return matches
}

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * @description
 * search streets by matching keywords
 * @param {array} keywords - list of keywords to be searched
 */
function _searchStreetsByKeywords(keywords) {
  // ["dole","ba"] => /\bdole.*\b(?:ba.*|bratislava(?:\b|\s))
  const re = new RegExp(keywords.map(_regexpKeywordWithAliases).join(''))
  const matches = []
  for(let i=0; i<geo_streets.length; i++) {
    const street = geo_streets[i]
    const indexes = street.f // get list of precalculated site search strings combined from site name, street and city
    for(let j=0; j<indexes.length; j++) {
      if(re.test(indexes[j])) {
        // if one of index strings match, create result as street info
        const match = {
          id: street.x+'.'+i,
          type: 'street',
          street: street.s,
          city: street.c,
          center: street.x,
          shape: _decompressShape(street.x, street.w),
          addresses: street.a || [],
        }
        matches.push(match)
        break
      }
    }
  }

  return matches
} // _searchStreetsByKeywords

////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * @description
 * search sites by matching keywords
 * @param {array} keywords - list of keywords to be searched
 */
function _searchSitesByKeyword(keywords) {
  // ["dole","ba"] => /\bdole.*\b(?:ba.*|bratislava(?:\b|\s))
  const re = new RegExp(keywords.map(_regexpKeywordWithAliases).join(''))
  const matches = []
  for(let i=0; i<geo_sites.length; i++) {
    const site = geo_sites[i]
    const indexes = site.f // get list of precalculated site search strings combined from site name, street and city
    for(let j=0; j<indexes.length; j++) {
      if(re.test(indexes[j])) {
        // if one of index strings match, create result as site info + associated address info
        const match = {
          id: site.x+'.'+site.id,
          center: site.x,
          name: site.n,
          type: site.t,
        }
        const addr = geo.getAddress(site.a || site.x, 0)
        if(addr) {
          match.city = addr.city
          match.street = addr.street
          match.streetno = addr.streetno
          if(addr.shape) match.shape = addr.shape
        }
        matches.push(match)
        break
      }
    }
  }

  return matches
} // _searchSitesByKeyword
